import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { LuSave, LuPlus } from 'react-icons/lu';

import {
  FormTextField,
  Button,
  TableLookup,
  Tag,
  FormSelectField,
} from '_components/_core';

import ValidationSchema from './utilities/ValidationSchema';

import {
  CustomCard,
  CustomCardBody,
  CustomCardFooter,
  CustomCardHeader,
} from '../../../../styles';
import { SPECIAL_COLUMNS } from '_components/_core/Table';

const tableConfig = {
  defaultFilters: {},
  defaultSorting: { field: 'name', order: 'asc' },
  defaultPagination: { currentPage: 1, itemsPerPage: 10 },
};

const ChargeStatus = ({
  onCreateAdminChargeStatus,
  onUpdateAdminChargeStatus,
  onDeleteAdminChargeStatus,
  onFetchAdminChargeStatus,
  chargeStatus,
  isLoading,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [currentChargeStatus, setCurrentChargeStatus] = useState(null);

  useEffect(() => {
    onFetchAdminChargeStatus();
  }, [onFetchAdminChargeStatus]);

  const handleSubmitForm = useCallback(
    (values, { resetForm }) => {
      if (isEditing && currentChargeStatus) {
        onUpdateAdminChargeStatus(currentChargeStatus.id, values, () => {
          setIsEditing(false);
          setCurrentChargeStatus(null);

          onFetchAdminChargeStatus();
        });
      } else {
        onCreateAdminChargeStatus(values, () => {
          onFetchAdminChargeStatus();
        });
      }
      resetForm();
    },
    [
      isEditing,
      currentChargeStatus,
      onCreateAdminChargeStatus,
      onUpdateAdminChargeStatus,
      onFetchAdminChargeStatus,
    ]
  );

  const handleEditChargeStatus = useCallback(
    (selectedChargeStatus) => {
      setCurrentChargeStatus(
        chargeStatus.find((item) => item.id === selectedChargeStatus)
      );
      setIsEditing(true);
    },
    [chargeStatus]
  );

  const handleCancelEdit = useCallback((resetForm) => {
    setIsEditing(false);
    setCurrentChargeStatus(null);
    resetForm();
  }, []);

  const handleConfirmDelete = useCallback(
    (method_id) => {
      onDeleteAdminChargeStatus(method_id, () => {
        onFetchAdminChargeStatus();
      });
    },
    [onDeleteAdminChargeStatus, onFetchAdminChargeStatus]
  );

  const columns = [
    {
      label: 'Ordem',
      keyName: 'sort_order',
      formatter: (value) => {
        if (value === 0 || !value) {
          return 0;
        }

        return value;
      },
    },
    {
      label: 'Status',
      keyName: 'description',
    },
    {
      label: 'Variant',
      keyName: 'variant',
    },
    {
      label: 'Pré-visualização',
      keyName: 'description',
      formatter: (value, row) => {
        return <Tag variant={row.variant}>{value}</Tag>;
      },
    },
    {
      label: 'Ações',
      keyName: 'id',
      specialType: SPECIAL_COLUMNS.ACTIONS,
      align: 'center',
    },
  ];

  return (
    <>
      <Formik
        initialValues={{
          id: currentChargeStatus?.id || null,
          description: currentChargeStatus?.description || '',
          variant: currentChargeStatus?.variant || '',
          sort_order: currentChargeStatus?.sort_order || 0,
        }}
        enableReinitialize
        validationSchema={ValidationSchema}
        onSubmit={handleSubmitForm}
      >
        {({ handleSubmit, errors, touched, resetForm }) => (
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col>
                <CustomCard noBorders>
                  <CustomCardHeader>
                    <h3>
                      {isEditing
                        ? 'Editar Status de Cobrança'
                        : 'Adicionar Status de Cobrança'}
                    </h3>
                  </CustomCardHeader>
                  <CustomCardBody>
                    <Form.Row>
                      <Col md={3}>
                        <Form.Group className="m-0 d-flex align-items-center">
                          <Form.Label className="m-0 mt-2">
                            Descrição
                          </Form.Label>
                        </Form.Group>
                      </Col>
                      <Col md={9}>
                        <Form.Group>
                          <FormTextField
                            name="description"
                            placeholder="Descrição do status de cobrança"
                            className={`form-control ${errors.description && touched.description ? 'is-invalid' : ''}`}
                          />
                        </Form.Group>
                      </Col>
                    </Form.Row>
                    <Form.Row>
                      <Col md={3}>
                        <Form.Group className="m-0 d-flex align-items-center">
                          <Form.Label className="m-0 mt-2">Variante</Form.Label>
                        </Form.Group>
                      </Col>
                      <Col md={9}>
                        <Form.Group>
                          <FormSelectField
                            name="variant"
                            placeholder="Variante do status de cobrança"
                            options={[
                              { value: 'default', label: 'default' },
                              { value: 'primary', label: 'primary' },
                              { value: 'info', label: 'info' },
                              { value: 'warning', label: 'warning' },
                              { value: 'success', label: 'success' },
                              { value: 'danger', label: 'danger' },
                            ]}
                            isSearchable={false}
                          />
                        </Form.Group>
                      </Col>
                    </Form.Row>

                    <Form.Row>
                      <Col md={3}>
                        <Form.Group className="m-0 d-flex align-items-center">
                          <Form.Label className="m-0 mt-2">Ordem</Form.Label>
                        </Form.Group>
                      </Col>
                      <Col md={9}>
                        <Form.Group>
                          <FormTextField
                            name="sort_order"
                            placeholder="Ordem do status de cobrança"
                            className={`form-control ${errors.sort_order && touched.sort_order ? 'is-invalid' : ''}`}
                          />
                        </Form.Group>
                      </Col>
                    </Form.Row>
                  </CustomCardBody>
                  <CustomCardFooter className="d-flex justify-content-center align-items-center">
                    <Button
                      variant="success"
                      onClick={handleSubmit}
                      isLoading={isLoading}
                      disabled={isLoading}
                      className="d-flex align-items-center"
                    >
                      {isEditing ? 'Atualizar' : 'Adicionar'}
                      {isEditing ? (
                        <LuSave className="ml-2" />
                      ) : (
                        <LuPlus className="ml-2" />
                      )}
                    </Button>
                    <Button
                      variant="link"
                      type="button"
                      size="sm"
                      disabled={isLoading}
                      onClick={() => handleCancelEdit(resetForm)}
                    >
                      Cancelar
                    </Button>
                  </CustomCardFooter>
                </CustomCard>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
      <Row className="mt-4">
        <Col>
          <TableLookup
            keyName="id"
            name="charge-status-lookup-table"
            columns={columns}
            data={chargeStatus || []}
            isLoading={isLoading}
            onEdit={handleEditChargeStatus}
            onDelete={handleConfirmDelete}
            {...tableConfig}
          />
        </Col>
      </Row>
    </>
  );
};

ChargeStatus.propTypes = {
  onCreateAdminChargeStatus: PropTypes.func.isRequired,
  onUpdateAdminChargeStatus: PropTypes.func.isRequired,
  onDeleteAdminChargeStatus: PropTypes.func.isRequired,
  onFetchAdminChargeStatus: PropTypes.func.isRequired,
  chargeStatus: PropTypes.array,
  isLoading: PropTypes.bool,
};

export default ChargeStatus;
